import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import { Box, Button } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { omit } from 'remeda';
import { DynamicForm, GenericDialog } from 'src/components';
import {
  Address,
  mapClient,
  useAddress,
  useBrandOptions,
  useCreateAddress,
  usePartnersOptions,
  usePlatformUsersOptions,
} from 'src/graphql';
import { NullableDeepPartial } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { ClientRaw } from 'src/types/api-types';
import { useNotifications } from 'src/utils/hooks';
import { AppEvents, emitAppEvent } from 'src/utils/hooks/use-app-events';
import {
  AddSiteFormFields,
  getClientOptionLabel,
  NewAddressFormValues,
  statesOptions,
  useNewSiteForm,
} from './use-new-site-form';

export const AddSiteModal = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();

  const { modalProps } = useModalState(Modals.AddSiteModal);
  const {
    closeModal,
    showModal: showAddSiteModal,
    setModalProps: setAddSiteModalProps,
  } = useModalMethods(Modals.AddSiteModal);

  const [loading, setLoading] = useState(false);

  const { showModal: showAddClientModal } = useModalMethods(Modals.AddClientModal);
  const {
    filters,
    limit,
    sort,
    offset,
    onSuccessSubmit,
    onCloseModal,
    prevFormValues,
    closeOnSiteCreate,
  } = modalProps ?? {};

  const client = modalProps?.client;
  const address = modalProps?.address as Address;

  const [formKey, setFormKey] = useState(0);

  const { showSuccessAlert, showErrorAlert } = useNotifications();

  const { createAddress } = useCreateAddress({
    sort,
    filters,
    limit,
    offset,
  });
  const { updateAddress } = useAddress(address?.addressId);

  const { associateClientsWithAddress } = useAddress();

  const partnerId = useMemo(
    () =>
      address?.partnerId ?? modalProps?.partnerId ?? client?.partnerId ?? selectedPartnerId,
    [address?.partnerId, client?.partnerId, modalProps?.partnerId, selectedPartnerId],
  );

  const userFilters = useMemo(() => {
    const filters = [
      {
        attribute: 'isActive',
        operator: '=',
        value: 'true',
      },
    ];

    if (address?.ownerId) {
      filters.push({
        attribute: 'userId',
        operator: '=',
        value: address?.ownerId,
      });
    }

    return filters;
  }, [address?.ownerId]);

  const { allUserRecords: currentUsers } = usePlatformUsersOptions(
    {
      partnerId,
      shouldIncludePlatformPartnerId: false,
      limit: 1,
      filters: userFilters,
    },
    {
      skip: !address?.ownerId,
    },
  );

  const optionBrands = useBrandOptions(partnerId, {
    desc: true,
  });

  useEffect(() => {
    // can be removed after refactoring the
    // dynamic form to prevent initialize
    // defaultValues before they are ready
    setFormKey((prevKey) => prevKey + 1);
  }, [optionBrands]);

  const partnerOptions = usePartnersOptions();

  const defaultFormValues = useMemo(() => {
    const result = {} as NewAddressFormValues;

    if (address) {
      const state = statesOptions.find((state) => state.value === address.state) || {
        label: '',
        value: '',
      };

      const accountManager = currentUsers.find((user) => user.id === address.ownerId) || {
        id: '',
        label: '',
        value: '',
      };

      const clientSegment = optionBrands.find(
        (brand) => brand.id === (address.brandId ?? address.brand?.id),
      ) || {
        label: '',
        id: '',
        data: null,
      };

      const partner = partnerOptions.find((partner) => partner.id === address.partnerId);

      result[AddSiteFormFields.AccountManager] = accountManager;
      result[AddSiteFormFields.ClientSegment] = clientSegment;
      result[AddSiteFormFields.State] = state;
      result[AddSiteFormFields.Partner] = partner;
      result[AddSiteFormFields.Clients] = [];
    }
    if (client) {
      const clientOptions = client
        ? [
            {
              id: client.id,
              label: getClientOptionLabel({
                fullName: client.name,
                primaryEmail: client.email,
              }),
            },
          ]
        : [];

      result[AddSiteFormFields.Clients] = clientOptions;
    }
    return { ...(address || {}), ...result };
  }, [client, address, optionBrands, currentUsers, partnerOptions]);

  const submitButtonRef = useRef<HTMLButtonElement | null>(null);
  const triggerSubmit = useCallback(() => {
    submitButtonRef?.current?.click();
  }, []);

  const formValues = (prevFormValues as NewAddressFormValues) || defaultFormValues;

  const [formMethods, setFormMethods] = useState<ReturnType<typeof useForm> | null>(null);

  const restoreAddSiteModal = useCallback(
    (result?: NullableDeepPartial<ClientRaw>) => {
      const currentFormValues = formMethods?.getValues?.();
      if (currentFormValues) {
        showAddSiteModal({
          prevFormValues: {
            ...currentFormValues,
            ...(!!result && {
              [AddSiteFormFields.Clients]: [
                ...((currentFormValues[
                  AddSiteFormFields.Clients
                ] as NewAddressFormValues[AddSiteFormFields.Clients]) || []),
                {
                  id: result.clientId,
                  label: getClientOptionLabel(mapClient(result as ClientRaw)),
                  value: result.clientId,
                },
              ],
            }),
          },
        });
      }
    },
    [formMethods, showAddSiteModal],
  );

  const addNewClient = useCallback(
    (partnerId: number) => {
      showAddClientModal({
        partnerId,
        onCloseModal: (result?: NullableDeepPartial<ClientRaw>) => {
          restoreAddSiteModal(result);
          if (modalProps) {
            setAddSiteModalProps(omit(modalProps, ['prevFormValues']));
          }
        },
      });
    },
    [modalProps, restoreAddSiteModal, setAddSiteModalProps, showAddClientModal],
  );

  const onAddNewClient = useCallback(() => {
    const currentPartnerId =
      partnerId ??
      (formMethods?.getValues?.(AddSiteFormFields.Partner) as { id?: number })?.id;
    if (currentPartnerId) {
      addNewClient(currentPartnerId);
    }
  }, [partnerId, formMethods, addNewClient]);

  const { newSiteFormFields, clearFields } = useNewSiteForm({
    partnerId,
    defaultValues: formValues,
    isUpdateForm: !!address,
    onAddNewClient,
    setFormValue: formMethods?.setValue,
  });

  const closeAddSiteModal = useCallback(
    (runOnCloseCallback = false) => {
      closeModal();
      clearFields();

      if (runOnCloseCallback) {
        onCloseModal?.();
      }
    },
    [clearFields, closeModal, onCloseModal],
  );

  const handleAddSite = useCallback(
    async (values: NewAddressFormValues) => {
      const currentPartnerId = values.partner?.id ?? partnerId;

      let updatedAddress = address;
      const addressCommonValues = {
        [AddSiteFormFields.Address1]: values.address1,
        [AddSiteFormFields.Address2]: values.address2,
        [AddSiteFormFields.BusinessName]: values.businessName,
        [AddSiteFormFields.City]: values.city,
        [AddSiteFormFields.State]: values.state?.value,
        [AddSiteFormFields.OrgNotes]: values.orgNotes,
        [AddSiteFormFields.Zip]: values.zip,
        ownerId: values.accountManager?.id,
        brandId: values.clientSegment?.id,
      };

      if (closeOnSiteCreate) {
        setLoading(true);
      } else {
        closeAddSiteModal();
      }

      if (updatedAddress) {
        updatedAddress = (
          await updateAddress({
            ...updatedAddress,
            ...addressCommonValues,
            platformUser: values.accountManager,
          })
        )?.[0] as Address;
      } else {
        const newAddress = await createAddress({
          ...addressCommonValues,
          partnerId: currentPartnerId,
        });
        if (newAddress) {
          updatedAddress = newAddress;
        } else {
          // `useCreateAddress` surfaces the user-facing error message (including duplicate-address errors).
          return;
        }
      }

      if (!updatedAddress) {
        return;
      }

      // If we are updating the address the relationships should already exist
      if (!address && values.clients?.length) {
        const clientIds = values.clients.map((client) => client.id);
        const result = await associateClientsWithAddress({
          clientIds,
          addressId: updatedAddress.addressId,
          partnerId: currentPartnerId,
        });
        updatedAddress = {
          ...updatedAddress,
          primaryClientId: values.clients[0]?.id,
        };

        if (!result) {
          showErrorAlert('Failed to associate clients with address');
        }
        emitAppEvent(AppEvents.ClientsToAddressAssociation, {
          address: updatedAddress,
          clientIds,
        });
      }

      if (closeOnSiteCreate) {
        closeAddSiteModal();
      }
      onSuccessSubmit?.({
        ...updatedAddress,
        brand: updatedAddress.brand || values[AddSiteFormFields.ClientSegment].data,
      });
      showSuccessAlert(address ? 'Site was updated' : 'Site was created');
    },
    [
      partnerId,
      address,
      closeOnSiteCreate,
      onSuccessSubmit,
      showSuccessAlert,
      closeAddSiteModal,
      updateAddress,
      createAddress,
      showErrorAlert,
      associateClientsWithAddress,
    ],
  );

  const actionButtons = useMemo(
    () => (
      <LoadingButton
        onClick={triggerSubmit}
        disabled={loading}
        loading={loading}
        loadingPosition={loading ? 'end' : undefined}
        endIcon={loading ? <SaveIcon /> : null}
        sx={{
          color: (theme) => theme.palette.custom.OV.sys.primary,
        }}
      >
        {address?.addressId ? 'Update Site' : 'Create Site'}
      </LoadingButton>
    ),
    [loading, triggerSubmit, address?.addressId],
  );

  return (
    <GenericDialog
      draggable
      modal={Modals.AddSiteModal}
      title={address ? 'Update Site' : 'Add Site'}
      onClose={() => closeAddSiteModal(true)}
      actionButtons={actionButtons}
    >
      <Box py={1}>
        <DynamicForm
          key={`${address?.addressId}-${formKey}`}
          style={{ gap: '1rem' }}
          onSubmit={handleAddSite}
          fields={newSiteFormFields}
          value={formValues}
          setFormMethods={setFormMethods}
          disabled={loading}
        >
          <Button
            type="submit"
            ref={submitButtonRef}
            sx={{
              display: 'none',
            }}
          >
            {address?.addressId ? 'Update Site' : 'Create Site'}
          </Button>
        </DynamicForm>
      </Box>
    </GenericDialog>
  );
};
